import type { CompanyDataResponse, DateTypeDJ } from '../@types/Company';
import { CheckState } from '../components/common/TriStateCheckbox/checkState';

export const FEDERATIONS = ['fnaim', 'unis', 'snpi'] as const;
export type Federation = typeof FEDERATIONS[number];

export const ACTIVITIES = ['Syndic', 'Gestion locative', 'Transaction', 'Location'] as const;
export type Activity = typeof ACTIVITIES[number];

export const DATE_FIN_CARTE_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: '1m', label: 'Expire dans moins d\'1 mois' },
  { value: '3m', label: 'Expire dans moins de 3 mois' },
  { value: '6m', label: 'Expire dans moins de 6 mois' },
  { value: '1y', label: 'Expire dans moins d\'1 an' },
] as const;

const DATE_FIN_CARTE_MONTHS: Record<string, number> = { '1m': 1, '3m': 3, '6m': 6, '1y': 12 };

export interface CompanyListFilters {
  federations: Record<Federation, CheckState>;
  activities: Record<Activity, CheckState>;
  minFederations: string;
  nbLotsHabUnder: string;
  nbLotsHabOver: string;
  nbLotsTotalOver: string;
  nbLotsTotalUnder: string;
  minActivities: string;
  negativeKeywords: string[];
  dateFinCarteFilter: string;
}

export type CompanyListDetails = Partial<CompanyDataResponse> & Record<string, unknown>;

/** What the company list filters read: a company of our base or a card holder of the CCI file. */
export interface FilterableCompany {
  name: string;
  siren: string | null;
  details: CompanyListDetails;
  /** DJ activities only, see getDjActivities. */
  activities: string[];
  nbLotsHab: number;
  nbLotsTotal: number;
}

export const createCompanyListFilters = (): CompanyListFilters => ({
  federations: {
    fnaim: CheckState.UNCHECKED,
    unis: CheckState.UNCHECKED,
    snpi: CheckState.UNCHECKED,
  },
  activities: {
    Syndic: CheckState.UNCHECKED,
    'Gestion locative': CheckState.UNCHECKED,
    Transaction: CheckState.UNCHECKED,
    Location: CheckState.UNCHECKED,
  },
  minFederations: '0',
  nbLotsHabUnder: '',
  nbLotsHabOver: '',
  nbLotsTotalOver: '',
  nbLotsTotalUnder: '',
  minActivities: '0',
  negativeKeywords: [],
  dateFinCarteFilter: '',
});

export const getDjActivities = (details: CompanyListDetails): string[] => [
  details.syndic && 'Syndic',
  details.gesLoc && 'Gestion locative',
  details.transa && 'Transaction',
  details.loc && 'Location',
].filter((activity): activity is string => typeof activity === 'string');

/** Badges of the list: DJ activities, then the CCI mentions DJ does not already state. */
export const getActivityLabels = (details: CompanyListDetails): string[] => [
  ...getDjActivities(details),
  !details.syndic && details.syndicPappers && 'Syndic (CCI)',
  !details.gesLoc && details.gestionImmobilierePappers && 'Gestion locative (CCI)',
  !details.transa && details.transactionPappers && 'Transaction (CCI)',
].filter((activity): activity is string => typeof activity === 'string');

/** Activities as the filters see them: DJ first, CCI mention otherwise. */
const getFilterActivities = (details: CompanyListDetails): Record<Activity, unknown> => ({
  Syndic: details.syndic || details.syndicPappers,
  'Gestion locative': details.gesLoc || details.gestionImmobilierePappers,
  Transaction: details.transa || details.transactionPappers,
  Location: details.loc,
});

/** A date matches a range when it falls before now + range: cards already expired are kept. */
export const isWithinDateRange = (
  dateStr: string | DateTypeDJ | null | undefined,
  range: string,
  now: Date = new Date(),
): boolean => {
  if (!range) {return true;}
  if (!dateStr) {return false;}

  const dateString = typeof dateStr === 'string' ? dateStr : dateStr.date;
  if (!dateString) {return false;}

  const horizon = new Date(now);
  horizon.setMonth(horizon.getMonth() + DATE_FIN_CARTE_MONTHS[range]);

  return new Date(dateString) <= horizon;
};

const toBound = (value: string): number | null => (value ? parseInt(value, 10) : null);

export const filterCompanies = <T extends FilterableCompany>(
  companies: T[],
  filters: CompanyListFilters,
  now: Date = new Date(),
): T[] => {
  const minFederations = parseInt(filters.minFederations, 10);
  const minActivities = parseInt(filters.minActivities, 10);
  const nbLotsHabUnder = toBound(filters.nbLotsHabUnder);
  const nbLotsHabOver = toBound(filters.nbLotsHabOver);
  const nbLotsTotalUnder = toBound(filters.nbLotsTotalUnder);
  const nbLotsTotalOver = toBound(filters.nbLotsTotalOver);

  return companies.filter((company) => {
    let federationCount = 0;
    for (const federation of FEDERATIONS) {
      const isMember = company.details[federation];
      if (isMember === true) {
        federationCount++;
      }
      const state = filters.federations[federation];
      if (state === CheckState.CHECKED && !isMember) {return false;}
      if (state === CheckState.EXCLUDED && isMember) {return false;}
    }
    if (federationCount < minFederations) {return false;}

    const activities = getFilterActivities(company.details);
    for (const activity of ACTIVITIES) {
      const state = filters.activities[activity];
      if (state === CheckState.CHECKED && !activities[activity]) {return false;}
      if (state === CheckState.EXCLUDED && activities[activity]) {return false;}
    }

    if (nbLotsHabUnder !== null && company.nbLotsHab > nbLotsHabUnder) {return false;}
    if (nbLotsHabOver !== null && company.nbLotsHab < nbLotsHabOver) {return false;}
    if (nbLotsTotalOver !== null && company.nbLotsTotal < nbLotsTotalOver) {return false;}
    if (nbLotsTotalUnder !== null && company.nbLotsTotal > nbLotsTotalUnder) {return false;}

    if (company.activities.length < minActivities) {return false;}

    if (filters.negativeKeywords.length > 0) {
      const name = company.name.toLowerCase();
      const siren = (company.siren ?? '').toLowerCase();
      if (filters.negativeKeywords.some((keyword) => name.includes(keyword) || siren.includes(keyword))) {
        return false;
      }
    }

    if (filters.dateFinCarteFilter && !isWithinDateRange(company.details.dateFinCarte, filters.dateFinCarteFilter, now)) {
      return false;
    }

    return true;
  });
};
